import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { ApiProblem } from '@/types/api'

let accessToken: string | null = null
let refreshAuthentication: (() => Promise<boolean>) | null = null

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '',
  timeout: 15_000,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
})

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!(error instanceof AxiosError) || error.response?.status !== 401 || !error.config) {
      return Promise.reject(error)
    }

    const config = error.config as RetryableRequestConfig
    const requestUrl = config.url ?? ''
    if (
      config._authRetry ||
      requestUrl.includes('/api/v1/auth/login') ||
      requestUrl.includes('/api/v1/auth/refresh') ||
      requestUrl.includes('/api/v1/auth/logout') ||
      !refreshAuthentication
    ) {
      return Promise.reject(error)
    }

    config._authRetry = true
    if (!(await refreshAuthentication())) {
      return Promise.reject(error)
    }
    return api.request(config)
  },
)

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function setAuthenticationRefresher(refresher: () => Promise<boolean>): void {
  refreshAuthentication = refresher
}

export function problemFrom(error: unknown): ApiProblem | null {
  if (!(error instanceof AxiosError) || !error.response?.data) {
    return null
  }
  const candidate = error.response.data as Partial<ApiProblem>
  return typeof candidate.code === 'string' ? (candidate as ApiProblem) : null
}
